<script setup lang="ts">
  import { computed, inject, ref, useCssModule, useSlots } from 'vue';

  import { MODULE_INJECTION } from '../Module/Module.keys';
  import { ModuleVariant } from '../Module/Module.types';

  defineOptions({
    name: 'll-module-header',
  });

  export interface ModuleHeaderProps {
    /**
     * Header title. Will get overridden by the `title` slot if provided.
     */
    title?: string;

    /**
     * Size of the header.
     *
     * - `small` = `h4`
     * - `medium` = `h3`
     * - `large` = `h2`
     */
    size?: 'small' | 'medium' | 'large';

    /**
     * Header description, aka subtitle. Will get overridden by the `description` slot if provided.
     */
    description?: string;

    /**
     * Stop the actions slot from force-wrapping on medium and smalls screens.
     */
    preventActionsWrap?: boolean;

    /**
     * Displays a border at the bottom
     */
    underline?: boolean;
  }

  const props = withDefaults(defineProps<ModuleHeaderProps>(), {
    title: '',
    size: 'medium',
    description: '',
    preventActionsWrap: false,
    underline: false,
  });
  const classes = useCssModule();
  const slots = useSlots();

  // get the module variant from the parent module
  const moduleVariant = ref();
  const moduleInjectedValues = inject(MODULE_INJECTION.key, MODULE_INJECTION.defaults);
  if (!moduleInjectedValues.variant) {
    throw new Error('<ModuleHeader> must be used within a <Module> component.');
  }
  moduleVariant.value = moduleInjectedValues.variant.value;

  const is = computed(() => {
    let component: string;

    switch (props.size) {
      case 'small':
        component = 'h4';
        break;

      case 'medium':
        component = 'h3';
        break;

      case 'large':
        component = 'h2';
        break;

      default:
        component = 'h3';
        break;
    }

    return component;
  });

  const isDefaultSlotSimpleString = computed(() => {
    if (!slots.default) {
      return false;
    }

    const defaultSlot = slots.default({});
    return (
      defaultSlot.length === 1 &&
      typeof slots.default({})[0].children === 'string' &&
      typeof slots.default({})[0].type === 'symbol'
    );
  });

  const marginBottomClasses = computed(() => {
    const isBoxOrTableOrList = ['box', 'table', 'list'].includes(moduleVariant.value);

    if (isBoxOrTableOrList && !props.underline) {
      return 'mb-3 lg:mb-6';
    } else if (isBoxOrTableOrList && props.underline) {
      return 'mb-3';
    } else {
      return '';
    }
  });
</script>

<template>
  <header
    class="stash-module-header flex flex-wrap justify-between"
    :class="{
      'rounded-t border-b border-ice-500 bg-ice-100 p-3 lg:px-6': moduleVariant === ModuleVariant.Card,
      'mb-6 rounded bg-white px-3 pt-3 shadow lg:mb-0 lg:rounded-b-none lg:px-6 lg:pt-6 lg:shadow-none':
        moduleVariant === ModuleVariant.List,
      'mb-3 border-b border-ice-200 lg:mb-6': props.underline,
      'mb-6 rounded-t bg-white px-3 pt-3 lg:mb-0 lg:px-6 lg:pt-6': moduleVariant === ModuleVariant.Table,
    }"
    data-test="stash-module-header"
  >
    <div class="stash-module-header__content mr-6" :class="marginBottomClasses" data-test="stash-module-header|content">
      <div class="flex items-center">
        <div
          v-if="slots.media && props.size === 'large'"
          class="stash-module-header__content__media mr-3"
          :class="classes.media"
          data-test="stash-module-header|content-media"
        >
          <!-- @slot Place an illustration that sits next to the header title & description. -->
          <slot name="media"></slot>
        </div>

        <div class="stash-module-header__content__text" data-test="stash-module-header|content-text">
          <!--
            @slot Header title. **Deprecated**: Use the default slot or the `title` prop instead.
          -->
          <slot v-if="slots.title" name="title"></slot>

          <template v-else-if="isDefaultSlotSimpleString">
            <component
              :is="is"
              class="stash-module-header__default_slot_string my-0"
              data-test="stash-module-header|default-slot-text"
            >
              <!-- @slot Header title. -->
              <slot></slot>
            </component>
          </template>
          <!-- @slot Header title. Falls back to the `title` prop. -->
          <slot v-else>
            <component :is="is" class="my-0">{{ props.title }}</component>
          </slot>

          <!--
            @slot Header description, aka subtitle. Falls back to the `description` prop.
          -->
          <slot name="description">
            <p v-if="props.description" class="mb-0 mt-3">{{ props.description }}</p>
          </slot>
        </div>
      </div>
    </div>

    <!-- todo: how can we pass a prop/class to actions in order to top align vs middle align? -->
    <div
      v-if="slots.actions"
      class="stash-module-header__actions self-center"
      :class="[
        marginBottomClasses,
        {
          'w-full lg:w-auto': !props.preventActionsWrap,
        },
      ]"
      data-test="stash-module-header|actions"
    >
      <!-- @slot Add action buttons or other CTAs. -->
      <slot name="actions"></slot>
    </div>
  </header>
</template>

<style module>
  /* max size for media/marketing illustration icons we would be passing to the slot */
  .media {
    max-height: 58px;
    max-width: 58px;
  }
</style>
